/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { Grid, FormHelperText } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
    DrawerBody,
    DrawerHeading,
    DrawerFooter,
    CreateTagButton,
    TagMultiselect,
    UncontrolledGenericDrawer
} from '@/components/common';
import { TextField, CurrencyValueInput } from '@/components/rhf-mui';
import {
    useToastNotification,
    useCurrency,
    useUrlParams,
    useDrawers
} from '@/hooks';
import {
    forcePositiveNumberInput,
    toWholeCurrency,
    toIntegerCurrency
} from '@/util';
import { ProductsActive, SaveIcon } from '@/resources/icons';
import { closeDrawer } from '@/state/actions';
import {
    PRODUCT_TYPE_PRODUCT,
    TAG_BASE_PARAM,
    PRODUCT_BASE_PARAM
} from '@/constants';
import {
    addProduct,
    updateProduct,
    getPaginatedProducts,
    getPaginatedTags
} from '@/modules/dataWrangler/dataWrangler';
import useStyles from './styles';

const ProductDrawer = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { triggerToast } = useToastNotification();
    const [saving, setSaving] = useState(false);

    const location = useLocation();
    const { getUrlParams } = useUrlParams();
    const currentProdUrlParams = getUrlParams({ base: PRODUCT_BASE_PARAM });
    const currentTagUrlParams = getUrlParams({ base: TAG_BASE_PARAM });

    const { getDrawer } = useDrawers();

    const methods = useForm({
        shouldUseNativeValidation: false
    });

    const { setValue, handleSubmit, watch, clearErrors } = methods;

    const allProducts =
        useSelector((state) => state.user?.activeBusiness?.products) || [];
    const productDrawer = getDrawer('product');
    const currency = useCurrency();

    const product = productDrawer?.product;
    const edit = productDrawer?.edit;
    const updatedTags = productDrawer?.updatedTags;
    const currentValues = productDrawer?.currentValues;
    const drawerOpen = Boolean(productDrawer);

    const handleCloseDrawer = () => {
        dispatch(closeDrawer('product'));
        clearErrors();
    };

    const checkIfUnique = (newName) => {
        const exists = allProducts.find(
            (previous) =>
                previous.name === newName &&
                previous.productId !== product?.productId
        );

        return !exists;
    };

    useEffect(() => {
        setValue('name', currentValues?.name || product?.name || '');
        setValue(
            'price',
            currentValues?.price || toWholeCurrency(product?.price)
        );
        setValue(
            'description',
            currentValues?.description || product?.description || ''
        );
        setValue('quantity', currentValues?.quantity || product?.quantity || 0);
        setValue('tags', updatedTags || product?.tags || []);
    }, [setValue, currentValues, updatedTags, product, drawerOpen]);

    const onSubmit = async (data) => {
        setSaving(true);

        const newProduct = {
            name: data.name,
            price: toIntegerCurrency(Number(data.price)),
            type: PRODUCT_TYPE_PRODUCT,
            description: data.description,
            quantity: parseFloat(data.quantity),
            // Strip out only the tagIds to add to product object
            tagIds: data?.tags?.map((tagData) => tagData?.tagId)
        };

        if (edit && product?.productId) {
            await updateProduct({
                ...newProduct,
                productId: product.productId
            });
        } else {
            await addProduct({
                ...newProduct
            });
        }

        if (location.pathname === '/items/products') {
            getPaginatedProducts({
                args: currentProdUrlParams,
                type: 'product'
            });
        }

        if (location.pathname === '/items/tags') {
            getPaginatedTags(currentTagUrlParams);
        }

        setSaving(false);
        handleCloseDrawer();

        triggerToast({
            message: edit
                ? `Product "${data.name}" updated`
                : `Product "${data.name}" saved`,
            action: 'saved',
            icon: <SaveIcon />
        });
    };

    return (
        <UncontrolledGenericDrawer
            open={drawerOpen}
            close={handleCloseDrawer}
            id="product"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className={classes.form}
                >
                    <div>
                        <DrawerHeading
                            startAdornment={<ProductsActive />}
                            title={edit ? 'Edit Product' : 'Add Product'}
                        />
                        <DrawerBody className={classes.itemList}>
                            <Grid item>
                                <TextField
                                    fullWidth
                                    name="name"
                                    id="product-drawer-name"
                                    label="Product Name"
                                    rules={{
                                        required: 'A product name is required',
                                        validate: {
                                            unique: (value) =>
                                                checkIfUnique(value) ||
                                                'Product names must be unique'
                                        }
                                    }}
                                    inputProps={{ maxLength: 256 }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    multiline
                                    fullWidth
                                    minRows={4}
                                    name="description"
                                    id="product-drawer-description"
                                    label="Product Description"
                                    rules={{
                                        maxLength: {
                                            value: 50,
                                            message:
                                                'Description exceeds maximum length'
                                        }
                                    }}
                                    inputProps={{ maxLength: 50 }}
                                />

                                <FormHelperText
                                    className={classes.descriptionHelperText}
                                >
                                    {`${watch('description')?.length}/50`}
                                </FormHelperText>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={2}>
                                    <Grid item xs={7}>
                                        <CurrencyValueInput
                                            currency={currency}
                                            label="Price"
                                            id="product-drawer-price"
                                            name="price"
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={5}>
                                        <TextField
                                            fullWidth
                                            name="quantity"
                                            label="Quantity"
                                            id="product-drawer-quantity"
                                            type="number"
                                            inputProps={{
                                                step: '0.01',
                                                min: '0'
                                            }}
                                            rules={{
                                                required:
                                                    'Please enter a valid number',
                                                pattern: {
                                                    value: /^-?\d*\.?\d*$/,
                                                    message:
                                                        'Please enter a valid number'
                                                }
                                            }}
                                            onKeyDown={forcePositiveNumberInput}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <TagMultiselect />
                            </Grid>
                            <Grid item container justifyContent="flex-end">
                                <CreateTagButton
                                    type="product"
                                    item={product}
                                    currentValues={watch()}
                                    edit={edit}
                                />
                            </Grid>
                        </DrawerBody>
                    </div>
                    <DrawerFooter
                        submitButtonId="Addproduct"
                        acceptOptions={{
                            label: 'Save',
                            type: 'submit'
                        }}
                        cancelOptions={{
                            label: 'Cancel',
                            action: handleCloseDrawer
                        }}
                        saving={saving}
                    />
                </form>
            </FormProvider>
        </UncontrolledGenericDrawer>
    );
};

export default ProductDrawer;
